// API関連の型定義

export interface QuotaWarning {
  accepted_tokens: number;
  rejected_tokens: number;
  message: string;
  usage_id: string | null;
  rejected_id: string | null;
}

export interface QuotaInfo {
  monthly_limit: number;
  used_tokens: number;
  remaining_tokens: number;
  usage_percentage: number;
}

export interface AIQuotaCheckResponse {
  status: 'ok' | 'warning' | 'quota_exceeded';
  quota: QuotaInfo;
  message?: string;
  reset_date: string;
}

export interface APIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp?: string;
  quota_warning?: QuotaWarning;
}

export interface APIError {
  code: string;
  message: string;
  details?: unknown;
  timestamp: string;
}

export interface UploadProgressEvent {
  loaded: number;
  total: number;
  percentage: number;
}

export interface APIConfig {
  baseURL: string;
  timeout: number;
  headers: Record<string, string>;
}

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  // IMPORTANT: Access import.meta.env properties directly in the condition
  // to prevent Vite from inlining the values during build
  const rawViteApiUrl: unknown = import.meta.env.VITE_API_URL;
  const viteApiUrl = typeof rawViteApiUrl === 'string' ? rawViteApiUrl : undefined;
  
  // Check if VITE_API_URL is explicitly set (including empty string)
  if (viteApiUrl !== undefined) {
    console.log('Using VITE_API_URL:', viteApiUrl || '(empty - relative paths)');
    return viteApiUrl;
  }
  
  // Use relative paths for CloudFront deployment (same domain) only if no explicit URL
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    console.log('Current hostname:', hostname);
    
    // CloudFront or production - use relative paths
    if (hostname.includes('cloudfront.net') || hostname.includes('d20ev1itslwrqq') || hostname.includes('d3efabuzgx5knn') || hostname.includes('s3-website')) {
      console.log('CloudFront/S3 detected, no VITE_API_URL set - using relative API paths');
      return ''; // Empty string for relative paths
    }
  }
  
  // Default to localhost for development
  console.log('Using localhost for development');
  return 'http://localhost:8000';
};

// Export the function to be called dynamically
export const getApiConfig = (): APIConfig => ({
  baseURL: getApiBaseUrl(),
  timeout: 600000, // 10 minutes for LLM processing
  headers: {
    'Content-Type': 'application/json',
  }
});

// For backward compatibility
export const DEFAULT_API_CONFIG = getApiConfig();

// API エンドポイント定義
export const API_ENDPOINTS = {
  UPLOAD_FILES: '/api/upload-bcp',
  GENERATE_BCP: '/api/bcp/generate',
  EXPORT_BCP: '/api/bcp/export',
  HEALTH: '/api/health',
  AI_CONSULT: '/api/ai-consult',
  AI_QUOTA_CHECK: '/api/ai/quota/check',
  SUMMARY_GENERATE: '/api/bcp/summary/generate'
} as const;

export type APIEndpoint = typeof API_ENDPOINTS[keyof typeof API_ENDPOINTS];
